/**
 * @lastAudited 2025-01-19
 * @auditStatus PASS
 * @auditNotes Pre-publish checklist passed. No issues found.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useVehicles } from '../../hooks/useVehicles';
import VehicleCard from '../inventory/components/VehicleCard';
import { useSEOHead } from '../../hooks/useSEOHead';
import Breadcrumb from '../../components/feature/Breadcrumb';
import { injectJsonLd, removeJsonLd } from '../../lib/schemaBuilder';

export default function UsedCarsTillamookPage() {
  useSEOHead({
    title: 'Used Cars Tillamook OR — Work Trucks, No Dealer Games | Rockhound Auto Sales',
    description: 'Buy quality used cars in Tillamook, Oregon with free delivery. Work trucks, SUVs, reliable vehicles for farming and fishing. Transparent pricing, no dealership pressure. Serving Tillamook County.',
    keywords: 'used cars tillamook oregon, cars for sale tillamook, tillamook auto sales, work trucks tillamook, manzanita cars, rockaway beach vehicles',
    canonicalUrl: 'https://rockhoundauto.com/used-cars-tillamook-oregon',
    geoPosition: '45.4562;-123.8440',
    geoRegion: 'US-OR',
    geoPlacename: 'Tillamook, Oregon',
  });

  const { vehicles, loading } = useVehicles();
  const [selectedType, setSelectedType] = useState<string>('all');

  const filteredVehicles = vehicles
    .filter(v => v.status === 'available')
    .filter(v => selectedType === 'all' || v.type === selectedType)
    .slice(0, 6);

  const faqs = [
    {
      question: "Why do Tillamook residents buy cars from Rockhound Auto?",
      answer: "Tillamook buyers choose Rockhound Auto because we offer transparent pricing without dealership pressure, free delivery to Tillamook County, and a fast buying process. We're located in Warrenton, about an hour north, but we bring vehicles to you for test drives at your home or workplace."
    },
    {
      question: "What types of vehicles are most popular in Tillamook Oregon?",
      answer: "Tillamook residents prefer work trucks like Toyota Tacoma, Ford F-150, and Chevrolet Silverado for farming, fishing, and hauling. SUVs with AWD like Subaru Outback and Toyota RAV4 are also popular for coastal weather and rural roads. Reliability and towing capacity matter most."
    },
    {
      question: "Do you deliver used cars to Tillamook for free?",
      answer: "Yes, we offer free delivery anywhere in Tillamook County including Tillamook, Manzanita, Rockaway Beach, Garibaldi, Bay City, and Wheeler. We bring the vehicle to your location for a test drive. If you love it, we complete paperwork on-site. No pressure or obligation."
    },
    {
      question: "How long does it take to buy a car from Rockhound Auto?",
      answer: "Most Tillamook customers complete the entire purchase in 30 to 60 minutes, including paperwork and financing. You can also take days to decide—we work on your timeline, not ours. No dealership pressure tactics or wasted weekends at sales lots."
    },
    {
      question: "What makes buying a car in Tillamook different from traditional dealerships?",
      answer: "Traditional dealerships require driving to Portland, Salem, or Longview and spending hours negotiating with surprise fees. Rockhound Auto brings vehicles to Tillamook, offers transparent pricing online, and completes deals fast without pressure or hidden costs. We respect your time and work schedule."
    },
    {
      question: "Are your used cars inspected and ready for rural Oregon roads?",
      answer: "Every vehicle is inspected for rural durability including rust protection, suspension, and mechanical reliability. We understand coastal weather, unpaved roads, and towing needs. All vehicles are detailed and ready for Tillamook County conditions before delivery."
    },
    {
      question: "Can I get financing for a used car in Tillamook Oregon?",
      answer: "Yes, we work with local credit unions and lenders who understand rural Oregon and working-class communities. We help Tillamook residents secure competitive financing, including options for trade-ins and flexible terms. No finance office pressure or unnecessary extended warranties."
    },
    {
      question: "What's the best vehicle for Tillamook farming and fishing work?",
      answer: "For Tillamook work needs, we recommend Toyota Tacoma or Ford F-150 trucks with 4WD and towing capacity. These offer reliability, rust resistance, and durability for farming, fishing, hauling equipment, and accessing boat launches. They hold value incredibly well and have affordable parts."
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  useEffect(() => {
    injectJsonLd('schema-tillamook-faq', faqSchema);

    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: 'https://rockhoundauto.com/'
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Used Cars',
          item: 'https://rockhoundauto.com/inventory'
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: 'Tillamook Oregon',
          item: 'https://rockhoundauto.com/used-cars-tillamook-oregon'
        }
      ]
    };

    injectJsonLd('schema-tillamook-breadcrumb', breadcrumbSchema);

    return () => {
      removeJsonLd('schema-tillamook-faq');
      removeJsonLd('schema-tillamook-breadcrumb');
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Breadcrumb items={[{ label: 'Home', path: '/' }, { label: 'Used Cars Tillamook Oregon' }]} />

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://public.readdy.ai/ai/img_res/ce2a64f526004cd836b3f275f9401b8c.jpg')] bg-cover bg-center opacity-20"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Used Cars for Tillamook That Can Handle the Dairy Loop
            </h1>
            <p className="text-xl sm:text-2xl text-slate-200 mb-8 leading-relaxed">
              We're up in Warrenton, but we deliver to Tillamook County. Every vehicle is inspected for coastal durability — because if it can't handle the fog, the curves, and the occasional cow in the road, it's not worth your time.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/inventory"
                className="inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-car-line text-xl"></i>
                Browse Inventory
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all border border-white/20 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line text-xl"></i>
                Get in Touch
              </Link>
            </div>
            
            <div className="mt-12 grid grid-cols-3 gap-6">
              <div>
                <div className="text-3xl font-bold text-teal-400 mb-1">Free</div>
                <div className="text-sm text-slate-300">Delivery to Tillamook</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-teal-400 mb-1">100%</div>
                <div className="text-sm text-slate-300">Coastal-Ready</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-teal-400 mb-1">Zero</div>
                <div className="text-sm text-slate-300">Dealer Markup</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Tillamook Buyers Choose Us */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Built for Tillamook County Roads
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              We know what your car goes through here — winding coastal highways, dairy farm backroads, and weather that changes every 10 minutes. Every vehicle is inspected with that reality in mind. Browse our <Link to="/inventory" className="text-teal-700 font-semibold underline underline-offset-2 hover:text-teal-900 transition-colors">used cars near Tillamook</Link> to find your next work truck or family SUV.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                  <i className="ri-shield-check-line text-2xl text-teal-600"></i>
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Inspected for Coastal Life</h3>
                <p className="text-slate-600 leading-relaxed">
                  We check for rust, seal integrity, and electrical issues caused by salt air and constant moisture. If it's been sitting in a beach parking lot without proper care, we pass. You need a car that'll survive Tillamook winters.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                  <i className="ri-truck-line text-2xl text-teal-600"></i>
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Free Delivery to Your Door</h3>
                <p className="text-slate-600 leading-relaxed">
                  Live in Tillamook, Manzanita, or anywhere in between? We'll bring the car to you. No need to coordinate rides or take a day off. We handle the logistics so you can focus on the fun part — driving your new car.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                  <i className="ri-price-tag-3-line text-2xl text-teal-600"></i>
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">No Dealer Markup Games</h3>
                <p className="text-slate-600 leading-relaxed">
                  We don't play the "market adjustment" game or tack on mystery fees. The price you see online is the price you pay. No surprises, no pressure, no BS. Just honest pricing from people who live on the coast too.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                  <i className="ri-user-heart-line text-2xl text-teal-600"></i>
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Locals Helping Locals</h3>
                <p className="text-slate-600 leading-relaxed">
                  We're not some Portland dealer trying to dump inventory on rural buyers. We live on the coast. We know the roads, the weather, and what actually holds up. If we sell you junk, we'll see you at the Tillamook Creamery. Reputation matters.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why People Buy Here */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-slate-900">
              Why Tillamook Residents Buy Cars Here
            </h2>
            <div className="bg-gradient-to-br from-teal-50 to-cyan-50 border-l-4 border-teal-500 p-8 rounded-r-xl mb-8">
              <p className="text-lg text-slate-800 leading-relaxed">
                Tillamook is working-class Oregon at its best—dairy farms, fishing, forestry, and people who value hard work, straight talk, and fair deals. Buying a car shouldn't mean driving to Portland or Longview and wasting a Saturday at a dealership. Rockhound Auto brings quality used vehicles to the <Link to="/service-areas/tillamook" className="text-teal-700 font-semibold underline underline-offset-2 hover:text-teal-900 transition-colors">Tillamook service area</Link> with free delivery, transparent pricing, and a buying process that respects your time and work schedule.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-truck-line text-2xl text-teal-600"></i>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Work Trucks for Real Work</h3>
                <p className="text-slate-700">
                  We stock reliable pickups built for farming, fishing, construction, and hauling—Toyota Tacoma, Ford F-150, Chevrolet Silverado. Vehicles with towing capacity, 4WD, and durability for Tillamook County work.
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-price-tag-3-line text-2xl text-teal-600"></i>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Straight Pricing Always</h3>
                <p className="text-slate-700">
                  The price online is the real price. No negotiation games, no "manager approval" needed, no surprise fees in the finance office. What you see is what you pay—simple and honest.
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-home-smile-line text-2xl text-teal-600"></i>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Free Delivery to Tillamook County</h3>
                <p className="text-slate-700">
                  We deliver vehicles to your Tillamook location for test drives—farm, home, or workplace. Drive on your roads, test towing capacity, check cargo space. If you love it, we complete paperwork on-site.
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-time-line text-2xl text-teal-600"></i>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Fast Process, Your Timeline</h3>
                <p className="text-slate-700">
                  Most Tillamook customers complete the entire purchase in 30-60 minutes. Or take days to decide—we work on your timeline. No dealership pressure, no wasted weekends, no high-pressure sales tactics.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why They Drive to Rockhound */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-slate-900">
              Why Tillamook Buyers Choose Rockhound Auto
            </h2>
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-l-4 border-amber-500 p-8 rounded-r-xl mb-8">
              <p className="text-lg text-slate-800 leading-relaxed">
                Tillamook families and workers choose Rockhound Auto because we're not a traditional dealership. We're an auto broker in Warrenton, about an hour north, offering free delivery across the entire <Link to="/service-areas/tillamook" className="text-amber-700 font-semibold underline underline-offset-2 hover:text-amber-900 transition-colors">Tillamook service area</Link>. No sales floor pressure, no finance office tricks, no wasting your Saturday when you could be working, fishing, or spending time with family.
              </p>
            </div>

            <div className="space-y-6">
              <div className="bg-white p-6 rounded-xl shadow-sm border-l-4 border-teal-500">
                <h3 className="text-xl font-bold text-slate-900 mb-3">We Bring Vehicles to You</h3>
                <p className="text-slate-700">
                  Browse inventory online from your Tillamook home or farm. Found something you like? We deliver it for a free test drive anywhere in the <Link to="/service-areas/tillamook" className="text-teal-700 font-semibold underline underline-offset-2 hover:text-teal-900 transition-colors">Tillamook service area</Link>. Drive it on your roads, test towing capacity, check cargo space. Love it? We complete paperwork on-site. Not right? No hard feelings.
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-sm border-l-4 border-teal-500">
                <h3 className="text-xl font-bold text-slate-900 mb-3">No Dealership Games</h3>
                <p className="text-slate-700">
                  Traditional dealerships play the four-square game, send you to a "manager" for approval, and hit you with surprise fees in finance. We don't. The price you see online is the real price. No negotiation, no tricks, no hidden costs.
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-sm border-l-4 border-teal-500">
                <h3 className="text-xl font-bold text-slate-900 mb-3">We Understand Rural Oregon</h3>
                <p className="text-slate-700">
                  Tillamook buyers need vehicles that handle coastal weather, unpaved roads, and real work. We inspect for rust resistance, suspension durability, and towing capacity. We stock trucks for farming and fishing, SUVs with AWD, and reliable vehicles built for rural life.
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-sm border-l-4 border-teal-500">
                <h3 className="text-xl font-bold text-slate-900 mb-3">Local Credit Union Financing</h3>
                <p className="text-slate-700">
                  We work with Oregon credit unions who understand rural communities and working-class families. Competitive rates, flexible terms, and no finance office pressure. We help with trade-ins and down payments. No extended warranties you don't need.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Dealer Insight */}
      <section className="py-16 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-user-star-line text-3xl text-white"></i>
              </div>
              <div>
                <h2 className="text-3xl font-bold mb-2">Dealer Insight</h2>
                <p className="text-teal-400 font-semibold">Local Market Knowledge</p>
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-8">
              <p className="text-xl leading-relaxed mb-6">
                "Tillamook is a working county where trucks dominate. Toyota Tacoma and Ford F-150 are the most popular because they're reliable, hold value incredibly well, and handle farming, fishing, and hauling without breaking down. Buyers also want AWD SUVs like Subaru Outback for coastal weather and rural roads. Reliability and towing capacity matter more than flash or luxury features."
              </p>
              <p className="text-slate-300 italic">
                — Rockhound Auto Sales, serving Tillamook County since 2020
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-12 text-slate-900 text-center">
              Frequently Asked Questions About Buying Used Cars in Tillamook Oregon
            </h2>
            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                  <h3 className="text-xl font-bold text-slate-900 mb-3">
                    {faq.question}
                  </h3>
                  <p className="text-slate-700 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Available Inventory */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 text-slate-900">
              Used Cars Available for Tillamook Delivery
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              All vehicles are inspected, detailed, and ready for free delivery to Tillamook County.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-3 mb-12">
            <button
              onClick={() => setSelectedType('all')}
              className={`px-6 py-2 rounded-full font-semibold transition-colors whitespace-nowrap ${
                selectedType === 'all'
                  ? 'bg-teal-600 text-white'
                  : 'bg-white text-slate-700 hover:bg-slate-100'
              }`}
            >
              All Vehicles
            </button>
            <button
              onClick={() => setSelectedType('Truck')}
              className={`px-6 py-2 rounded-full font-semibold transition-colors whitespace-nowrap ${
                selectedType === 'Truck'
                  ? 'bg-teal-600 text-white'
                  : 'bg-white text-slate-700 hover:bg-slate-100'
              }`}
            >
              Trucks
            </button>
            <button
              onClick={() => setSelectedType('SUV')}
              className={`px-6 py-2 rounded-full font-semibold transition-colors whitespace-nowrap ${
                selectedType === 'SUV'
                  ? 'bg-teal-600 text-white'
                  : 'bg-white text-slate-700 hover:bg-slate-100'
              }`}
            >
              SUVs
            </button>
            <button
              onClick={() => setSelectedType('Sedan')}
              className={`px-6 py-2 rounded-full font-semibold transition-colors whitespace-nowrap ${
                selectedType === 'Sedan'
                  ? 'bg-teal-600 text-white'
                  : 'bg-white text-slate-700 hover:bg-slate-100'
              }`}
            >
              Sedans
            </button>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
            </div>
          ) : filteredVehicles.length > 0 ? (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
                {filteredVehicles.map((vehicle) => (
                  <VehicleCard key={vehicle.id} vehicle={vehicle} />
                ))}
              </div>
              <div className="text-center">
                <Link
                  to="/inventory"
                  className="inline-block bg-teal-600 hover:bg-teal-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors whitespace-nowrap"
                >
                  View All Available Inventory
                </Link>
              </div>
            </>
          ) : (
            <div className="text-center py-12 bg-white rounded-xl">
              <i className="ri-car-line text-6xl text-slate-300 mb-4"></i>
              <p className="text-xl text-slate-600 mb-6">
                No vehicles currently match your filter. Check back soon or view all inventory.
              </p>
              <Link
                to="/inventory"
                className="inline-block bg-teal-600 hover:bg-teal-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors whitespace-nowrap"
              >
                View All Inventory
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Service Area Cross-Link */}
      <section className="py-10 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-teal-50 border border-teal-200 rounded-xl p-6 flex items-center justify-between gap-4 flex-wrap">
              <div>
                <h4 className="font-bold text-slate-900 mb-1">Tillamook Service Area Details</h4>
                <p className="text-slate-600 text-sm">
                  See our full Tillamook service area page — delivery zones, nearby cities we cover, and why coastal Oregon buyers choose Rockhound Auto over driving to Portland or Salem.
                </p>
              </div>
              <Link
                to="/service-areas/tillamook"
                className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-5 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap text-sm"
              >
                Tillamook Service Area <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-gradient-to-br from-teal-600 to-cyan-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">
            Ready to Buy a Car in Tillamook Oregon?
          </h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto opacity-90">
            Browse our inventory online, schedule a free test drive in Tillamook County, and complete the purchase in under an hour. No dealership pressure, no hidden fees, no wasted weekends.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/inventory"
              className="bg-white text-teal-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-slate-100 transition-colors whitespace-nowrap"
            >
              View Available Inventory
            </Link>
            <Link
              to="/contact"
              className="bg-teal-700 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-teal-800 transition-colors whitespace-nowrap border-2 border-white/30"
            >
              Schedule Test Drive
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}